import React, { Component } from "react";
import axios from "axios";
import AxiosConfig from "src/utilities/AxiosConfig";
import type { AppState } from "src/store";
import { connect } from "react-redux";
import mutator from "../mutator";
import taskManagerUtils from "./TaskManagerUtils/TaskManagerUtils";
import TaskManagerTaskViewer from "./TaskManagerTaskViewer/TaskManagerTaskViewer";
import TaskManagerTable from "./TaskManagerTable/TaskManagerTable";
import TasksManagerSearchTasks from "./TaskManagerSearchTasks/TaskManagerSearchTasks";
import type DynamicFormTaskTypes from "src/pages/dynamic_forms/Types/DynamicFormTaskTypes";
import TaskManagerTaskFiller from "./TaskManagerTaskFiller/TaskManagerTaskFiller";
import TaskManagerNoTasks from "./TaskManagerNoTasks/TaskManagerNoTasks";
import type DynamicFormPaletteTypes from "src/store/DyanmicFormPalette/types";
import type DynamicFormNormalizedReferenceTypes from "src/pages/dynamic_forms/Types/DynamicFormNormalizedReferenceTypes";
import snackBar from "src/utilities/snackBar/SnackBar";
import Loader from "src/components/common/Loader";
import "./TaskManager.css";
interface Props {
  loggedInUser: { userId: string | number; userName: string };
  targetedUserId: string | number;
  palette: Array<DynamicFormPaletteTypes>;
}
interface State {
  tasks: Array<number> | [];
  tasksCopy: Array<number> | [];
  viewTypes: Array<{
    id: number;
    active: boolean;
    title: string;
    endpoint: string;
    otherEndpoint: string | null;
    order: number;
  }>;
  taskToView: DynamicFormTaskTypes | null;
  taskToComplete: DynamicFormTaskTypes | null;
  formReference: DynamicFormNormalizedReferenceTypes | null;
  sortMap: {
    direction: "asc" | "desc" | string;
    type: "date" | "text" | "number" | string;
    key: string;
  };
  busy: boolean;
  searching: boolean;
}

class TaskManager extends Component<Props, State> {
  timeFormat = "MM/DD/YY h:mm a";
  date = new Date();
  timeZoneOffset = -this.date.getTimezoneOffset();

  viewTypes = [
    {
      id: 1,
      active: false,
      title: "Completed Tasks",
      endpoint: "GetDynamicFormCompeletedTasksByEmploye",
      otherEndpoint: null,
      order: 2,
    },
    {
      id: 2,
      active: true,
      title: "Active Tasks",
      endpoint: "GetDynamicFormActiveTasksByEmployee",
      otherEndpoint: null,
      order: 1,
    },
    {
      id: 3,
      active: false,
      title: "Completed Forms",
      endpoint: "GetDynamicFormAllCompletedFormsByEmployee",
      otherEndpoint: "/1",
      order: 3,
    },
  ].sort(({ order: a }, { order: b }) => a - b);

  state = {
    tasks: [],
    tasksCopy: [],
    viewTypes: this.viewTypes,
    taskToView: null,
    taskToComplete: null,
    formReference: null,
    sortMap: { direction: "asc", type: "date", key: "DueDateUTC" },
    busy: false,
    searching: false,
  };

  componentDidMount(): void {
    this.getReference();
  }

  manageState = (keys: Array<{ key: string; value?: any }>) => {
    this.setState(mutator.mutate(this.state, keys));
  };

  /* A function that takes a string and up to two arguments. It then calls a function with the name of the
    string and passes the two arguments. */
  callFunction = (functionToCall: string, arg1: any, arg2: any) => {
    //@ts-ignore
    this[functionToCall](arg1, arg2);
  };

  setView = (id: number) => {
    const viewTypes = this.viewTypes;
    let endpoint = "";
    let otherEndpoint: string | null = null;
    for (let i = 0; i < viewTypes.length; i++) {
      if (viewTypes[i].id === id) {
        viewTypes[i].active = true;
        endpoint = viewTypes[i].endpoint;
        otherEndpoint = viewTypes[i].otherEndpoint;
      } else {
        viewTypes[i].active = false;
      }
    }
    this.manageState([
      { key: "viewTypes", value: viewTypes },
      { key: "taskToView", value: null },
      { key: "taskToComplete", value: null },
      { key: "searching", value: false },
    ]);
    this.getTasks(endpoint, otherEndpoint, this.state.formReference);
  };

  getActiveView = () => {
    const viewTypes = this.state.viewTypes;
    const result = viewTypes.find((view) => {
      return view.active;
    });
    const activeView = result ? result : viewTypes[0];
    return activeView;
  };

  getReference = () => {
    if (!this.state.busy) {
      this.manageState([{ key: "busy", value: true }]);
    }
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/company/GetFormReference/${this.props.targetedUserId}`,
        AxiosConfig.configValue()
      )
      .then((res) => {
        const reference = res.data;
        reference.error = undefined;
        this.manageState([{ key: "formReference", value: reference }]);
        this.setView(this.getActiveView().id);
      })
      .catch(() => {
        snackBar({
          text: "Failed to fetch form reference",
          type: "error",
          timeout: 4000,
        });
        this.manageState([{ key: "busy", value: false }]);
      });
  };

  getTasks = (
    endpoint: string,
    otherEndpoint: string | null,
    reference: DynamicFormNormalizedReferenceTypes | null
  ) => {
    if (!this.state.busy) {
      this.manageState([{ key: "busy", value: true }]);
    }
    const formReference = reference ? reference : this.state.formReference;
    if (formReference) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/api/company/${endpoint}/${
            formReference.TargetUserId
          }${otherEndpoint ? `${otherEndpoint}` : ""}`,
          AxiosConfig.configValue()
        )
        .then((res) => {
          const tasks = res.data;
          const taskIds = [];
          for (let i = 0; i < tasks.length; i++) {
            taskIds.push(tasks[i].DynamicFormWorkFlowId);
          }
          //@ts-ignore
          taskManagerUtils.reference = formReference;
          taskManagerUtils.buildHashTable(tasks);
          this.manageState([
            { key: "tasks", value: taskIds },
            { key: "tasksCopy", value: taskIds },
            { key: "formReference", value: formReference },
            { key: "busy", value: false },
          ]);
        })
        .catch(() => {
          snackBar({
            text: "Failed to fetch tasks",
            type: "error",
            timeout: 4000,
          });
          this.manageState([{ key: "busy", value: false }]);
        });
    } else {
      setTimeout(() => {
        this.getReference();
      }, 500);
    }
  };

  render() {
    const props = this.props;
    const state = this.state;
    const activeView = this.getActiveView();
    //@ts-ignore
    const tasks = taskManagerUtils.sort(state.tasks, state.sortMap);
    const formReference = state.formReference;
    const busy = state.busy;
    const canRender = formReference && !busy;

    return (
      <div className="TaskManager">
        {canRender && (
          <TasksManagerSearchTasks
            tasks={tasks}
            tasksCopy={state.tasksCopy}
            viewTypes={state.viewTypes}
            activeView={activeView}
            manageParentState={this.manageState}
            callParentFunction={this.callFunction}
          />
        )}
        {canRender && (
          <TaskManagerTable
            tasks={tasks}
            activeView={activeView}
            sortMap={state.sortMap}
            timeFormat={this.timeFormat}
            timeZoneOffset={this.timeZoneOffset}
            manageParentState={this.manageState}
          />
        )}
        {state.taskToView && (
          //@ts-ignore
          <TaskManagerTaskViewer
            //@ts-ignore
            task={state.taskToView}
            manageParentState={this.manageState}
            //@ts-ignore
            formReference={formReference}
            loggedInUser={props.loggedInUser}
            palette={props.palette}
          />
        )}
        {state.taskToComplete && (
          //@ts-ignore
          <TaskManagerTaskFiller
            //@ts-ignore
            task={state.taskToComplete}
            callParentFunction={this.callFunction}
            manageParentState={this.manageState}
            activeViewId={activeView.id}
            //@ts-ignore
            formReference={formReference}
            loggedInUser={props.loggedInUser}
            palette={props.palette}
          />
        )}
        {busy && (
          <div className="TaskManagerBusy">
            <Loader title="Loading tasks" />
          </div>
        )}
        {!busy && state.tasks.length === 0 && !state.searching && (
          <TaskManagerNoTasks taskType={activeView.title} />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  loggedInUser: state.user,
  palette: state.dynamicFormPalette,
});
//@ts-ignore
export default connect(mapStateToProps)(TaskManager);
